import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useCopyToClipboard from 'react-use/lib/useCopyToClipboard.js';
import { styled } from '@linaria/react';
import { Button, Input } from '@livecommerce/design-system';
import { useAlert } from 'contexts/AlertContext.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';

const StyledPasswordRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-gap: 8px;
  align-items: flex-end;
  margin-top: 8px;
`;

const StyledButton = styled(Button)`
  padding-top: 11px;
  padding-bottom: 11px;
`;

// In case if this component is used in the future with react-hook-form,
// it should be wrapped with forwardRef and useCombinedRefs should be used to combine the refs.
const CopyText = ({ id, value, onCopied, ...props }) => {
  const { t } = useLocalization();
  const [_, copyToClipboard] = useCopyToClipboard();
  const inputRef = useRef(null);
  const { setAlert } = useAlert();

  const handlePasswordCopyClick = useCallback(() => {
    copyToClipboard(value);
    inputRef.current.select();

    setAlert({
      title: t('genericLabels.copiedAlertTitle'),
      variant: 'feedback',
    });

    onCopied?.();
  }, [copyToClipboard, value, setAlert, t, onCopied]);

  return (
    <StyledPasswordRow>
      <Input
        id={id}
        data-test-id={id}
        type="text"
        readOnly
        value={value}
        customRightButton={props.type === 'password' ? undefined : <div />}
        ref={inputRef}
        {...props}
      />
      <StyledButton variant="solidGrey" type="button" onClick={handlePasswordCopyClick}>
        {t('genericLabels.copy')}
      </StyledButton>
    </StyledPasswordRow>
  );
};

CopyText.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onCopied: PropTypes.func,
};

export default CopyText;
